import { Component, ElementRef, ViewChild } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Application } from 'app/shared/models/application';
import { tr, DialogBase, DialogService, HotkeyService, sort, DialogResult } from '@kpd/framework-ngx';
import { ApplicationService } from 'app/shared/services/application.service';
import { PermissionsetService } from 'app/shared/services/permissionset.service';
import { TreeNode } from 'primeng/components/common/treenode';
import { PermissionsetKey } from 'app/shared/models/keys/permissionset-key';
import { Permissionset } from 'app/shared/models/permissionset';
import { of, combineLatest, forkJoin, BehaviorSubject } from 'rxjs';
import { catchError, tap, filter, take } from 'rxjs/operators';

@Component({
  selector: 'app-permission-sets-import-wizard',
  templateUrl: './permissionsets-import-wizard.component.html',
})
export class PermissionsetsImportWizardComponent extends DialogBase {
  activeStep = 0;
  form: FormGroup;
  selectedApp: Application | null;
  applicationError: string | null;
  applications: Application[] = [];
  applicationColumns = [{ name: 'name', title: tr('Name') }];

  permissionSetSelection = {};
  permissionSetsTree: TreeNode[] = [] as TreeNode[];

  applicationConfirmed = false;
  importLoading$ = new BehaviorSubject<boolean>(false);

  feedback: { success: boolean; message: string; name: string }[] = [];
  selectOptions = [
    { value: 'assign', label: tr('Assign') },
    { value: 'use', label: tr('Use') },
    { value: 'both', label: tr('Both') },
  ];
  tenantPermissionSetsForApplication: Permissionset[];
  verify = DialogResult.Verify;
  cancel = DialogResult.Cancel;

  @ViewChild('feedbackScroll', { static: false })
  feedbackScroll: any;

  get countOfSetsToImport(): number {
    let count = 0;
    Object.keys(this.permissionSetSelection).forEach(key => {
      const types = (this.permissionSetsTree.find(node => node.data.key.toString() === key) as TreeNode).data.types;
      count += types.length;
    });
    return count;
  }

  constructor(
    dialogService: DialogService,
    element: ElementRef,
    hotKeyService: HotkeyService,
    private applicationService: ApplicationService,
    private permissionSetService: PermissionsetService,
  ) {
    super('wizard', dialogService, element, hotKeyService);
  }

  onBeforeShow(): void {
    this.createForm();
    this.permissionSetSelection = {};
    this.feedback = [];
    this.activeStep = 0;
    this.applicationService.getApplicationsByTenant().subscribe(apps => {
      this.applications = apps;
    });
  }

  private createForm(): void {
    this.form = new FormGroup({ application: new FormControl(null, [Validators.required]) });
  }

  onApplicationChange(): void {
    this.applicationError = null;
    if (!this.form.value.application) {
      return;
    }
    this.importLoading$.next(true);
    const getUserPermissionSets$ = this.permissionSetService.getUserPermissionSets(this.form.value.application.name);
    const getPermissionSetsForApplication$ = this.permissionSetService.getPermissionSetsForApplication(
      this.form.value.application.name,
    );
    forkJoin(getUserPermissionSets$, getPermissionSetsForApplication$).subscribe(
      ([permissionSets, result]) => {
        this.permissionSetsTree = permissionSets
          .filter(set => set.type === 'template')
          .map(set => {
            return {
              data: { ...set, types: [] },
              label: set.name,
              children: [],
              leaf: false,
              collapsed: true,
              styleClass: 'permissionsets-tree__set',
            } as TreeNode;
          });

        if (this.permissionSetsTree.length === 0) {
          this.applicationError = tr('Please select an application with templates you can manage.');
        }
        this.tenantPermissionSetsForApplication = result.result;
        this.importLoading$.next(false);
      },
      () => {
        this.importLoading$.next(false);
      },
      () => {
        this.importLoading$.next(false);
      },
    );
  }

  onConfirmApplication(): void {
    if (this.applicationError) {
      return;
    }
    if (this.form.invalid) {
      this.form['submitted'] = true;
      if (this.form.controls.application.errors) {
        if (this.form.controls.application.errors.required) {
          this.applicationError = tr('Please select an application.');
        }
        if (this.form.controls.application.errors.noTemplatePermissionset) {
          this.applicationError = tr('Please select an application with templates you can manage.');
        }
      }
      this.applicationError = null;
      return;
    }
    this.applicationConfirmed = true;
    // Wait for importLoading to become false
    this.importLoading$
      .pipe(
        filter(i => !i && this.applicationConfirmed),
        take(1),
      )
      .subscribe(() => {
        this.selectedApp = this.form.value.application;
        this.activeStep = 1;
        this.applicationConfirmed = false;
      });
  }
  onNodeExpand(event: any): void {
    if (!this.selectedApp) {
      return;
    }
    if (event.node) {
      this.permissionSetService
        .getById(new PermissionsetKey(event.node.data.name, this.selectedApp.name.toString(), true))
        .subscribe(permissionSetWithPermissions => {
          if (permissionSetWithPermissions) {
            event.node.children = this.createPermissionsNodes(permissionSetWithPermissions);
            event.node.data.permissions = permissionSetWithPermissions.permissions;
          }
        });
    }
  }

  createPermissionsNodes(set: Permissionset): TreeNode[] {
    if (!set.permissions || set.permissions.length === 0) {
      return [
        {
          data: undefined,
          leaf: true,
          selectable: false,
          styleClass: 'permissionsets-tree__permission',
          label: tr('No permissions linked to this permission set'),
          type: 'notFound',
        } as TreeNode,
      ];
    }
    return set.permissions.map(permission => {
      return {
        data: permission,
        selectable: false,
        leaf: true,
        label: permission.getDisplayName(),
        styleClass: 'permissionsets-tree__permission',
      } as TreeNode;
    });
  }

  onCheckboxChange(event: any, node: TreeNode): void {
    const permissionsetId: PermissionsetKey = node.data.key;
    if (!event.target.checked) {
      if (!this.permissionSetSelection[permissionsetId.toString()]) {
        return;
      }
      node.data.types = [];
      delete this.permissionSetSelection[permissionsetId.toString()];
      return;
    }
    this.permissionSetSelection[permissionsetId.toString()] = { key: permissionsetId };
    node.data.types = ['use'];
  }

  onSelectButtonChange(event: any, node: TreeNode): void {
    const permissionsetId = node.data.key;
    if (!this.permissionSetSelection[permissionsetId.toString()]) {
      this.permissionSetSelection[permissionsetId.toString()] = { key: permissionsetId };
      return;
    }
    node.data.types = event.value;
    if (event.value.length === 0) {
      delete this.permissionSetSelection[permissionsetId.toString()];
    }
  }

  onCheckboxChangeAll(event: any): void {
    if (event.target.checked) {
      this.permissionSetsTree.forEach(item => {
        if (!this.permissionSetSelection[item.data.key.toString()]) {
          this.permissionSetSelection[item.data.key.toString()] = { key: item.data.key };
        }
      });
      this.permissionSetsTree = this.permissionSetsTree.map(node => {
        node.data.types = ['use'];
        return node;
      });
      return;
    }
    this.permissionSetSelection = {};
    this.permissionSetsTree = this.permissionSetsTree.map(node => {
      node.data.types = [];
      return node;
    });
  }

  get permissionSetSelectionCount(): number {
    return Object.keys(this.permissionSetSelection).length;
  }

  getPermissionSetSelection(permissionsetId: PermissionsetKey): {} {
    if (!this.permissionSetSelection[permissionsetId.toString()]) {
      return { type: [] };
    }
    return this.permissionSetSelection[permissionsetId.toString()];
  }

  onStartImport(): void {
    this.importLoading$.next(true);
    this.activeStep = 2;

    const observers = Object.keys(this.permissionSetSelection).map(key => {
      const types = (this.permissionSetsTree.find(node => node.data.key.toString() === key) as TreeNode).data.types;
      return this.permissionSetService
        .importTemplate(
          this.permissionSetSelection[key].key,
          types,
          this.permissionSetSelection[key].key.permissionSetId,
          this.tenantPermissionSetsForApplication.map(t => t.name),
        )
        .pipe(
          tap(sets => {
            sets.forEach(set => {
              this.feedback.push({
                success: true,
                message: 'test',
                name: set.name,
              });
            });
          }),
          catchError((error: any) => {
            this.feedback.push({
              success: false,
              message: error.title || error,
              name: name,
            });
            setTimeout(() => {
              this.feedbackScroll.nativeElement.scrollTop = this.feedbackScroll.nativeElement.scrollHeight + 200;
            });
            return of();
          }),
          tap(() => {
            setTimeout(() => {
              this.feedbackScroll.nativeElement.scrollTop = this.feedbackScroll.nativeElement.scrollHeight + 200;
            });
          }),
        );
    });

    combineLatest(...observers).subscribe(
      () => {},
      () => {
        this.importLoading$.next(false);
      },
      () => {
        this.importLoading$.next(false);
      },
    );
  }

  get sortedFeedback(): any {
    return sort(this.feedback, [{ name: 'success' }]);
  }

  get feedbackAllSucceeded(): boolean {
    return this.feedback.length > 0 && this.feedback.every(item => item.success && item.success === true);
  }

  get feedbackHasErrors(): boolean {
    return this.feedback.some(item => !item.success);
  }

  get importErrors(): any {
    return this.feedback.filter(item => !item.success);
  }
}
